import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Divider,
  Drawer,
  Link,
  Typography,
  useMediaQuery,
} from '@mui/material';
import type { Theme } from '@mui/material';
import { DashboardSidebarSection } from './DashboardSidebarSection';
import { useLocation } from 'react-router-dom';
import { Scrollbar } from '../ScrollBar/Scrollbar';
import { useRootStore } from 'hooks/useRootStore';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import Co from 'constants/frontend/shared/Co';
import { observer } from 'mobx-react-lite';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useLayout } from 'contexts/layoutContext';
import AppVal from 'constants/frontend/app/AppVal';
import { getMenuCategory, getSectionsOfCategory } from 'menuStructures/pcMenuCategory';
import SettingDataStore from 'stores/shared/SettingDataStore';
import { MenuDomainSettingT } from 'types/frontend/Menu';

const t = (str: string) => str;
const settingDataStore = new SettingDataStore();

const DashboardSidebar = observer(() => {
  const { isSidebarOpen, setIsSidebarOpen } = useLayout();

  const userStore = useRootStore().userStore;

  const location = useLocation();
  const lgUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'), {
    noSsr: true,
  });

  const category = getMenuCategory(location.pathname);
  const [menuDomainSetting, setMenuDomainSetting] = useState<MenuDomainSettingT>({});

  useEffect(() => {
    if (category !== 'bill' || !userStore.isAssistantDirector || !userStore.isFinancialManager) {
      return;
    }

    const loadBillSetting = async () => {
      const result = await settingDataStore.readBillSetting();
      if (result?.isSuccess && result.object) {
        setMenuDomainSetting((prev) => ({
          ...prev,
          bill: {
            sumsAssistantDirectorVisible: result.object!.sumsAssistantDirectorVisible,
          },
        }));
      }
    };

    void loadBillSetting();
  }, [category, userStore.myInfoTimestamp]);

  const sections = useMemo(() => {
    if (!category) return [];

    return getSectionsOfCategory(category)
      .map((section) => ({
        title: section.title,
        items: section.items
          .filter(
            (item) =>
              !item.accessablePositions ||
              (userStore.myInfo?.teacherInfo?.teacher.position &&
                item.accessablePositions.includes(userStore.myInfo?.teacherInfo?.teacher.position)),
          )
          .filter((item) => !item.visibilityCriteria || item.visibilityCriteria(userStore.myInfo))
          .filter(
            (item) =>
              !item.domainVisibilityCriteria ||
              item.domainVisibilityCriteria(menuDomainSetting, userStore.myInfo),
          ),
      }))
      .filter((section) => section.items.length > 0);
  }, [category, t, userStore.myInfo?.teacherInfo, menuDomainSetting]);

  if (!category) return null;

  // 메뉴중 접근 불가능한거 추리기

  const academyName = userStore.myInfo?.teacherInfo?.academy.name;
  const titleInAcademy = Co.Academy.teacherPositions.get(
    userStore.myInfo?.teacherInfo?.teacher.position || 'regularTeacher',
  );

  const nickname = userStore.myInfo?.teacherInfo?.teacher.nickname || '사용자 정보 없음';

  const teacherDesc = `${nickname} (${titleInAcademy})`;

  const content = (
    <>
      <Scrollbar
        sx={{
          'height': '100%',
          '& .simplebar-content': {
            height: '100%',
          },
          'paddingTop': '64px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <div>
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Box sx={{ display: 'inline-flex', mb: 2 }}>
                <Avatar>
                  <AccountBalanceIcon />
                </Avatar>
              </Box>
              <Typography variant="h6" textAlign="center">
                {academyName}
              </Typography>
              <Typography variant="body1" color="textSecondary" textAlign="center">
                {teacherDesc}
              </Typography>

            </Box>
          </div>
          <Divider />
          <Box sx={{ flexGrow: 1 }}>
            {sections.map((section) => (
              <DashboardSidebarSection
                key={section.title}
                path={''}
                sx={{
                  'mt': 2,
                  '& + &': {
                    mt: 2,
                  },
                }}
                {...section}
              />
            ))}
          </Box>
          <Divider />
          <Box sx={{ p: 2 }}>
            <Link target="_blank" href="https://guide.anotherclass.co.kr">
              <Button
                color="trivialAction"
                variant="text"
                endIcon={<ArrowForwardIcon fontSize="medium" />}
              >
                {t('가이드 바로가기')}
              </Button>
            </Link>
          </Box>
        </Box>
      </Scrollbar>
    </>
  );

  if (lgUp) {
    // 큰 화면
    return (
      <Drawer
        anchor="left"
        open={isSidebarOpen}
        PaperProps={{
          sx: {
            backgroundColor: 'background.paper',
            borderRightColor: 'divider',
            borderRightStyle: 'solid',
            borderRightWidth: 1,
            color: 'text.primary',
            width: AppVal.SIDE_BAR_WIDTH,
          },
        }}
        // 항상 나와있게 하는건 permanant
        variant="persistent"
      >
        {content}
      </Drawer>
    );
  } else {
    // 작은 화면
    return (
      <Drawer
        anchor="left"
        onClose={() => setIsSidebarOpen(false)}
        open={isSidebarOpen}
        PaperProps={{
          sx: {
            backgroundColor: 'background.paper',
            color: 'text.primary',
            width: AppVal.SIDE_BAR_WIDTH,
          },
        }}
        sx={{ zIndex: (theme) => theme.zIndex.appBar + 100 }}
        variant="temporary"
      >
        {content}
      </Drawer>
    );
  }
});

export { DashboardSidebar };
